import {
  Box,
  Button,
  Chip,
  Icon,
  SxProps,
  Typography
} from "@outschool/backpack";
import {
  EnrollmentManagerCard_EnrollmentFragment,
  EnrollmentManagerCard_SectionFragment
} from "@outschool/gql-frontend-generated";
import { faGlobe } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { sectionLearnersPath, userConversationsPath } from "@outschool/routes";
import { timeZoneName } from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage } from "@outschool/ui-components-shared";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { EnrollmentStatusChip } from "../Chips/EnrollmentStatusChip";
import { LearnerBlockedBadge } from "../teacher/LearnerBlockedBadge";
import SidebarCard from "./SidebarCard";

type EnrollmentParentType = NonNullable<
  EnrollmentManagerCard_EnrollmentFragment["learner"]
>["parent"];
type EnrollmentLearnerType = Omit<
  NonNullable<EnrollmentManagerCard_EnrollmentFragment["learner"]>,
  "parent"
> & {
  parent?: EnrollmentParentType;
};
type EnrollmentType = Omit<
  EnrollmentManagerCard_EnrollmentFragment,
  "isWithdrawn" | "learner"
> & {
  isWithdrawn?: boolean;
  learner: EnrollmentLearnerType | null;
};

interface EnrollmentsListProps {
  enrollments: EnrollmentType[];
  onManageEnrollment?: () => void;
  section?: EnrollmentManagerCard_SectionFragment;
  showAges?: boolean;
  sx?: SxProps;
}

const EnrollmentsList = ({
  enrollments,
  onManageEnrollment,
  section,
  showAges
}: EnrollmentsListProps) => {
  const { currentUser } = useSession();
  const canManage =
    !!currentUser &&
    (currentUser.uid === section?.leader?.uid ||
      currentUser.uid === section?.activity?.leader?.uid);
  const { t } = useTranslation(
    "client\\components\\EnrolledClass\\EnrollmentManagerCard"
  );
  const Link = useLinkComponent();
  const enrollmentsWithLearners = enrollments.filter(e => !!e.learner);

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        paddingBottom: "1.5rem",
        borderBottomStyle: "solid",
        borderBottomWidth: "1px",
        borderBottomColor: "grey.100"
      }}
    >
      {enrollmentsWithLearners.map((enrollment, i) =>
        !enrollment.learner ? null : (
          <Box key={`learner-${i}`}>
            <Box
              sx={{
                flexDirection: "column"
              }}
            >
              <Box
                flex
                key={enrollment.learner.uid}
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  paddingBottom: ".5rem"
                }}
              >
                <AvatarImage
                  avatar={enrollment.learner.avatar}
                  name={enrollment.learner.name}
                  height={32}
                  width={32}
                />
                <Typography variant="h5">
                  {enrollment.learner.name}
                  {showAges && <> ({enrollment.learner.age})</>}
                </Typography>
                <Box
                  sx={{
                    marginLeft: "auto"
                  }}
                >
                  {enrollment.learner.isBlocked ? (
                    <LearnerBlockedBadge />
                  ) : (
                    <EnrollmentStatusChip
                      size="small"
                      enrollment={enrollment}
                      section={section}
                    />
                  )}
                </Box>
              </Box>
              {canManage && (
                <Box
                  flex
                  sx={{
                    marginTop: "1rem",
                    justifyContent: "space-between",
                    paddingBottom: "1.5rem"
                  }}
                >
                  <Box
                    flex
                    sx={{
                      flexDirection: "column",
                      alignItems: "start",
                      gap: "1rem"
                    }}
                  >
                    <Button
                      to={sectionLearnersPath(section!)}
                      variant="link"
                      component={Link}
                      sx={{ fontSize: "1.6rem" }}
                    >
                      {t("Message Learner")}
                    </Button>
                    {canManage && (
                      <Button
                        variant="link"
                        sx={{ fontSize: "1.6rem" }}
                        onClick={onManageEnrollment}
                      >
                        {t("Manage enrollment")}
                      </Button>
                    )}
                  </Box>
                  {enrollment.learner.parent?.timeZone && (
                    <Chip
                      size="small"
                      label={
                        <Box
                          sx={{ display: "flex", gap: 8, alignItems: "center" }}
                        >
                          <Icon icon={faGlobe} />
                          {timeZoneName(enrollment.learner.parent.timeZone)}
                        </Box>
                      }
                      color={"neutral"}
                    />
                  )}
                </Box>
              )}
            </Box>
            {canManage && !!enrollment.learner.parent && (
              <Box
                flex
                sx={{
                  flexDirection: "column",
                  gap: "0.5em",
                  paddingTop: "1.5rem",
                  borderTopStyle: "solid",
                  borderTopWidth: "1px",
                  borderTopColor: "grey.100"
                }}
              >
                <Typography>
                  {t("Parent: ")}
                  <Typography
                    sx={{
                      fontWeight: "fontWeightBold"
                    }}
                  >
                    {enrollment.learner.parent.name}
                  </Typography>
                </Typography>
                <Box>
                  <Button
                    to={userConversationsPath(
                      true,
                      enrollment.learner.parent.uid
                    )}
                    variant="link"
                    component={Link}
                    sx={{ fontSize: "1.6rem" }}
                  >
                    {t("Message Parent")}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )
      )}
    </Box>
  );
};

interface EnrollmentManagerProps {
  enrollments: EnrollmentType[];
  onManageEnrollment?: () => void;
  section?: EnrollmentManagerCard_SectionFragment;
  showAges?: boolean;
}
export default function EnrollmentManagerCard({
  enrollments,
  onManageEnrollment = () => {},
  section,
  showAges = false,
  children
}: React.PropsWithChildren<EnrollmentManagerProps>) {
  return (
    <SidebarCard>
      {enrollments.length > 0 ? (
        <EnrollmentsList
          enrollments={enrollments}
          onManageEnrollment={onManageEnrollment}
          section={section}
          showAges={showAges}
        />
      ) : null}
      {children && <Box sx={{ marginTop: "1rem" }}>{children}</Box>}
    </SidebarCard>
  );
}

EnrollmentManagerCard.fragments = {
  enrollment: gql`
    fragment EnrollmentManagerCard_enrollment on Enrollment {
      uid
      learner {
        uid
        avatar
        name
        age
        isBlocked
        parent {
          uid
          name
          timeZone
        }
      }
      ...EnrollmentStatusChip_enrollment
    }
    ${EnrollmentStatusChip.fragments.enrollment}
  `,
  section: gql`
    fragment EnrollmentManagerCard_section on Section {
      uid
      leader {
        uid
      }
      activity {
        hasTeacherSchedule
        leader {
          uid
          name
        }
      }
      ...EnrollmentStatusChip_section
    }
    ${EnrollmentStatusChip.fragments.section}
  `
};
